import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Users, Mail, Copy, Trash2, Loader2, ShieldCheck, Info } from "lucide-react";
import api, { apiError } from "@/lib/api";
import { TopBar } from "@/components/TopBar";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import { fmtDate } from "@/lib/format";

export default function Team() {
  const [data, setData] = useState(null);
  const [email, setEmail] = useState("");
  const [sending, setSending] = useState(false);

  const load = () => api.get("/team").then((r) => setData(r.data)).catch(() => toast.error("Failed to load team"));
  useEffect(() => { load(); }, []);

  const invite = async () => {
    if (!email.trim()) return toast.error("Enter an email");
    setSending(true);
    try {
      await api.post("/team/invites", { email: email.trim(), role: "admin" });
      toast.success("Invite created — copy the link to share");
      setEmail("");
      load();
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail));
    } finally { setSending(false); }
  };

  const copyLink = (link) => {
    const full = link.startsWith("http") ? link : `${window.location.origin}${link}`;
    navigator.clipboard.writeText(full).then(() => toast.success("Invite link copied")).catch(() => toast.error("Copy failed"));
  };

  const revoke = async (id) => {
    try { await api.delete(`/team/invites/${id}`); toast.success("Invite revoked"); load(); }
    catch (err) { toast.error(apiError(err.response?.data?.detail)); }
  };

  return (
    <div className="min-h-screen">
      <TopBar />
      <main className="mx-auto max-w-5xl px-4 py-8 sm:px-6 sm:py-10 lg:px-8">
        <p className="flex items-center gap-2 text-sm font-medium text-neutral-500"><Users className="h-4 w-4" /> Admin only</p>
        <h1 className="mt-1 font-display text-4xl font-extrabold tracking-tight sm:text-5xl">Team</h1>
        <p className="mt-2 max-w-xl text-neutral-500">Invite teammates as administrators. They set their own password from a single-use link — nobody ever sees or sets it for them. Admins see the full shared dashboard, including Budget Trackers.</p>

        <div data-testid="no-email-note" className="mt-5 flex items-start gap-2 rounded-xl border border-[#E7DFCC] bg-[#FFFDF8] px-4 py-3 text-sm text-neutral-600">
          <Info className="mt-0.5 h-4 w-4 shrink-0 text-[#35606E]" />
          No email service is connected, so invites aren’t emailed automatically. Create an invite below and copy its link to send each person yourself. Each link is single-use and expires in 14 days.
        </div>

        {/* Invite form */}
        <div className="pb-card mt-6 flex flex-col gap-3 p-5 sm:flex-row sm:items-end">
          <div className="flex-1">
            <label className="text-sm font-medium text-neutral-600">Invite by email</label>
            <Input data-testid="invite-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="name@label.com" onKeyDown={(e) => e.key === "Enter" && invite()} className="mt-1.5" />
          </div>
          <Button data-testid="invite-send" onClick={invite} disabled={sending} className="rounded-full bg-[#35606E] hover:bg-[#294b57]">
            {sending ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : <Mail className="mr-1.5 h-4 w-4" />} Create invite
          </Button>
        </div>

        {data === null && <Skeleton className="mt-6 h-40 rounded-2xl" />}

        {/* Pending invites */}
        {data && data.invites.length > 0 && (
          <section className="mt-8">
            <h2 className="font-display text-xl font-bold">Pending invites</h2>
            <div className="mt-3 flex flex-col gap-2">
              {data.invites.map((inv) => (
                <div key={inv.id} data-testid={`invite-${inv.id}`} className="pb-card flex flex-wrap items-center justify-between gap-3 p-4">
                  <div className="min-w-0">
                    <p className="truncate font-medium">{inv.email}</p>
                    <p className="text-xs text-neutral-500">{inv.role} · expires {fmtDate(inv.expires_at)}{inv.expired ? " · expired" : ""}</p>
                  </div>
                  <div className="flex items-center gap-2">
                    <code data-testid={`invite-link-${inv.id}`} className="hidden max-w-[260px] truncate rounded bg-neutral-100 px-2 py-1 text-xs text-neutral-500 sm:inline">{inv.link}</code>
                    <Button data-testid={`copy-invite-${inv.id}`} onClick={() => copyLink(inv.link)} variant="outline" size="sm" className="rounded-full"><Copy className="mr-1 h-3.5 w-3.5" /> Copy link</Button>
                    <button data-testid={`revoke-invite-${inv.id}`} onClick={() => revoke(inv.id)} className="rounded-full p-2 text-neutral-400 hover:bg-red-50 hover:text-red-600"><Trash2 className="h-4 w-4" /></button>
                  </div>
                </div>
              ))}
            </div>
          </section>
        )}

        {/* Members */}
        <section className="mt-8">
          <h2 className="font-display text-xl font-bold">Administrators</h2>
          <div className="mt-3 flex flex-col gap-2">
            {data?.members.map((m) => (
              <div key={m.id} data-testid={`member-${m.id}`} className="pb-card flex items-center justify-between gap-3 p-4">
                <div className="flex items-center gap-3">
                  <span className="grid h-9 w-9 place-items-center rounded-full bg-[#35606E] text-sm font-semibold text-white">{(m.name || m.email).slice(0, 1).toUpperCase()}</span>
                  <div><p className="font-medium">{m.name || m.email}</p><p className="text-xs text-neutral-500">{m.email}</p></div>
                </div>
                <span className="inline-flex items-center gap-1 rounded-full bg-emerald-50 px-2.5 py-0.5 text-xs font-medium text-emerald-700"><ShieldCheck className="h-3.5 w-3.5" /> Admin</span>
              </div>
            ))}
          </div>
        </section>
      </main>
    </div>
  );
}
